import ComponentGuard from '@/components/ui/custom/ComponentGuard';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import HiddenComponents from '@/components/ui/custom/HiddenComponents';
import StatisticsCard from '@/components/ui/custom/StatisticsCard';
import IconButton from '@/components/ui/custom/buttons/IconButton';
import AvatarName from '@/components/ui/custom/dataDisplay/AvatarName';
import Badge from '@/components/ui/custom/dataDisplay/Badge';
import Text from '@/components/ui/custom/dataDisplay/Text';
import DateRangePicker from '@/components/ui/custom/dataInput/DateRangePicker';
import InfiniteSearchInput from '@/components/ui/custom/dataInput/InfiniteSearchInput';
import Notifications from '@/components/notifications/card/Notifications';
import TodayLessons from '@/components/staff/Todaylessons/TodayLessons';
import AbsenceReports from '@/components/teacher/AbsenceReport/AbsenceReports';
import CollapsableCard from '@/components/teacher/CollapsableCard';
import useConfigContext from '@/data/hook/config';
import useAuth from '@/data/hook/useAuth';
import useInfiniteService from '@/data/hook/useInfiniteService';
import useListService from '@/data/hook/useListService';
import useStatisticService from '@/data/hook/useStatistics';
import {
  pendingSummariesQueryKeys,
  unitsQueryKeys,
  unitsStatisticsQueryKeys,
} from '@/data/services/querykeys';
import {
  UnitStatisticsFilters,
  updateUnitsStatistics,
} from '@/data/services/statisticsServices';
import { isSuperAdmin, isUnitAdmin } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';
import {
  TeacherReportStatistics,
  TeacherReportStatisticsCount,
} from '@/models/Statistics';
import { UserTypeEnum } from '@/models/User';
import { Breakpoints } from '@/utils/Breakpoint';
import { ClassNameComponent } from '@/utils/ClassNameComponent';
import { formatDateRange } from '@/utils/formatDateRange';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import {
  UnitCalculatorResult,
  unitStatisticsCalculator,
} from '@/utils/statistics/unitStatisticsCalculators';
import { DocumentChartBarIcon } from '@heroicons/react/24/outline';
import { ArrowPathIcon } from '@heroicons/react/20/solid';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { subDays } from 'date-fns';
import moment from 'moment';
import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { DateRange } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { createSearchParams } from 'react-router-dom';

export default function AdminDashboardView() {
  const { t: tDashboard } = useTranslation('translation', {
    keyPrefix: 'dashboard',
  });
  const { user } = useAuth();

  const isMultipleUnits =
    user && isUnitAdmin(user.userType) && user.unitsIds.length > 1;

  const [selectedUnit, setSelectedUnit] = useState<number | undefined>(
    isMultipleUnits || isSuperAdmin(user?.userType)
      ? undefined
      : user?.unitsIds[0],
  );

  const dateNow = new Date(Date.now());

  const defaultSelected: DateRange = {
    from: subDays(dateNow, 29),
    to: dateNow,
  };
  const unitIdFilter = getAuthorizedUnits(user);
  const unitId: number[] | undefined = selectedUnit
    ? [selectedUnit]
    : unitIdFilter;

  const [range, setRange] = useState<DateRange>(defaultSelected);

  const { from: dateAfter, to: dateBefore } = formatDateRange(range);

  const filters: UnitStatisticsFilters = {
    dateBefore,
    dateAfter,
    unitId,
  };

  const {
    statistic,
    calculated: unitStatistics,
    isInitialLoading: isLoadingUnitsStatistics,
  } = useStatisticService(
    unitsStatisticsQueryKeys.list(filters),
    unitStatisticsCalculator,
  );
  const { results: teacherReportsCount } = useListService({
    ...pendingSummariesQueryKeys.list({ unitId })._ctx.totalSum,
  });

  const {
    results: teacherReportList,
    isInitialLoading: isLoadingTeacherReports,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useInfiniteService({
    ...pendingSummariesQueryKeys.list({
      unitId: selectedUnit ? [selectedUnit] : undefined,
    })._ctx.infinity,
  });

  return (
    <div className="flex flex-col gap-4 min-h-full">
      <div className="flex flex-col gap-4">
        <Text
          text={tDashboard('hello') + user?.firstName + ' 👋'}
          className="text-primary"
          format="poppins-600"
          size="text-28"
        />
        <Text
          text={tDashboard('wellcome.admin')}
          format="rubik-400"
          size="text-16"
          className="break-words"
        />
      </div>
      <RenderDashboard
        selectedUnitId={selectedUnit}
        range={range}
        statistics={unitStatistics}
        hasNextPage={hasNextPage}
        fetchNextPage={fetchNextPage}
        isFetchingNextPage={isFetchingNextPage}
        isLoadingUnitsStatistics={isLoadingUnitsStatistics}
        setRange={setRange}
        updateDate={statistic?.updatedAt}
        setSelectedUnit={setSelectedUnit}
        isLoadingTeacherReports={isLoadingTeacherReports}
        teacherReportsData={{
          teacherReportList,
          teacherReportsCount,
        }}
      />
    </div>
  );
}

type TeacherReportsData = {
  teacherReportList: TeacherReportStatistics[];
  teacherReportsCount: TeacherReportStatisticsCount[];
};

interface RenderDashboardProps {
  selectedUnitId?: number;
  range?: DateRange;
  setRange: Dispatch<SetStateAction<DateRange>>;
  updateDate?: string | Date;
  statistics?: UnitCalculatorResult;
  setSelectedUnit: Dispatch<SetStateAction<number | undefined>>;
  isLoadingTeacherReports: boolean;
  teacherReportsData: TeacherReportsData;
  isLoadingUnitsStatistics: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
}

const RenderDashboard = ({
  selectedUnitId,
  range,
  setRange,
  updateDate,
  isLoadingTeacherReports,
  statistics,
  isLoadingUnitsStatistics,
  isFetchingNextPage,
  hasNextPage,
  fetchNextPage,
  teacherReportsData: { teacherReportList, teacherReportsCount },
  setSelectedUnit,
}: RenderDashboardProps) => {
  const ref = useRef<HTMLDivElement>(null);

  const queryClient = useQueryClient();
  const { t } = useTranslation('translation', {
    keyPrefix: 'adminDashboard',
  });
  const { user } = useAuth();

  const isMultipleUnits =
    user && isUnitAdmin(user?.userType) && user.unitsIds?.length > 1;

  const { mutate: updateStatistics, isLoading } = useMutation(
    updateUnitsStatistics,
    {
      onSuccess() {
        const statisticQueryKey = unitsStatisticsQueryKeys._def;
        queryClient.invalidateQueries(statisticQueryKey);
      },
    },
  );

  const classNameCard = { base: 'w-full max-h-full' };

  const { hiddenComponents, showComponent } = useConfigContext();

  if (user) {
    return (
      <div className="relative flex flex-col gap-2.5 h-full">
        <div
          className="relative grid grid-cols-1 md:grid-cols-2 gap-2"
          id="newRegistredDashboard"
        >
          <div className="flex flex-col gap-2 w-full">
            <DateRangePicker
              className="text-blue-600"
              range={range}
              setRange={setRange}
            />
            <RenderStatisticsCards
              isLoadingUnitsStatistics={isLoadingUnitsStatistics}
              statistics={statistics}
            />
            <div className="flex gap-x-2.5 pb-2.5 items-center text-primary">
              <IconButton
                className="w-6 h-6"
                loading={isLoading}
                onClick={() => updateStatistics({ unitId: selectedUnitId })}
                icon={<ArrowPathIcon className="w-6 h-6 cursor-pointer" />}
              />

              <Text
                className="text-primary"
                text={`${t('lastUpdated')} ${moment(updateDate).format(
                  'ddd, DD/MM/yyyy HH:mm',
                )}`}
              />
            </div>
            <div className="flex gap-3 flex-wrap w-full">
              <ConditionalRenderer
                condition={isMultipleUnits || isSuperAdmin(user?.userType)}
              >
                <InfiniteSearchInput
                  service={unitsQueryKeys.list}
                  filters={{
                    idIn: getAuthorizedUnits(user)?.toString(),
                    isActive: true,
                  }}
                  displayName={unit => unit.name}
                  input={{
                    testId: 'unitSelect',
                    placeholder: t('allUnits'),
                    className: {
                      base: 'w-full self-start bg-base-100 input-error',
                    },
                  }}
                  onSelect={unit => setSelectedUnit(unit.id)}
                  onDeselect={() => setSelectedUnit(undefined)}
                />
              </ConditionalRenderer>
              <PendingReports
                isLoading={isLoadingTeacherReports}
                teacherReportList={teacherReportList}
                teacherReportCount={teacherReportsCount}
                hasNextPage={hasNextPage}
                isFetchingNextPage={isFetchingNextPage}
                fetchNextPage={fetchNextPage}
                className={classNameCard}
              />
              <ComponentGuard roles={[UserTypeEnum.UNIT_ADMIN]}>
                <TodayLessons user={user} />
              </ComponentGuard>
            </div>
          </div>
          <div className="flex flex-col h-full gap-2">
            <Notifications className="h-[calc(100vh-140px)]" ref={ref} />
            <ComponentGuard roles={[UserTypeEnum.UNIT_ADMIN]}>
              <AbsenceReports className={classNameCard} />
            </ComponentGuard>
          </div>
        </div>
        <HiddenComponents
          render={!!hiddenComponents}
          sections={hiddenComponents}
          updateRender={showComponent}
        />
      </div>
    );
  }

  return null;
};

const RenderStatisticsCards = ({
  isLoadingUnitsStatistics,
  statistics,
}: {
  isLoadingUnitsStatistics: boolean;
  statistics?: UnitCalculatorResult;
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'adminDashboard',
  });

  const isXs = useMediaQuery({ minWidth: Breakpoints.xs });

  const size = isXs ? 'regular' : 'small';

  return (
    <div className="grid xs:grid-cols-2 sm:grid-cols-3 gap-2.5 w-full xs:w-fit justify-center">
      <StatisticsCard
        title={t('totalStudents')}
        testId="totalStudents"
        info={statistics?.totalStudents || 0}
        isLoading={isLoadingUnitsStatistics}
        className="bg-primary-content border text-primary px-1 py-1"
        size={size}
      />
      <StatisticsCard
        title={t('churnCount')}
        testId="churnCount"
        isLoading={isLoadingUnitsStatistics}
        info={(statistics?.churnRate ?? 0).toFixed(2) + '%'}
        className="bg-primary-content border text-primary"
        size={size}
      />

      <StatisticsCard
        title={t('totalKlasses')}
        testId="totalKlasses"
        isLoading={isLoadingUnitsStatistics}
        info={statistics?.totalKlasses || 0}
        className="bg-secondary-content border text-secondary"
        size={size}
      />
      <StatisticsCard
        title={t('studentsPerKlass')}
        testId="studentsPerKlass"
        isLoading={isLoadingUnitsStatistics}
        info={statistics?.studentsPerKlassCount || 0}
        className="bg-secondary-content border text-secondary"
        size={size}
      />

      <StatisticsCard
        title={t('totalTeachers')}
        testId="totalTeachers"
        info={statistics?.totalTeachers || 0}
        isLoading={isLoadingUnitsStatistics}
        className="bg-accent-content border text-accent hidden"
        size={size}
      />
      <StatisticsCard
        info={statistics?.studentPerTeacherCount || 0}
        title={t('studentsPerTeacher')}
        testId="studentsPerTeacher"
        isLoading={isLoadingUnitsStatistics}
        className="bg-accent-content border text-accent"
        size={size}
      />

      <StatisticsCard
        title={t('attendanceCount')}
        testId="attendanceCount"
        info={(statistics?.attendancePercentage ?? 0).toFixed(2) + '%'}
        isLoading={isLoadingUnitsStatistics}
        className="bg-primary-content border text-primary"
        size={size}
      />
    </div>
  );
};

interface PendingReportsProps {
  isLoading: boolean;
  isFetchingNextPage: boolean;
  hasNextPage?: boolean;
  fetchNextPage: () => void;
  teacherReportList: TeacherReportStatistics[];
  teacherReportCount: TeacherReportStatisticsCount[];
  className?: ClassNameComponent;
}

const PendingReports = ({
  isLoading,
  teacherReportList,
  fetchNextPage,
  teacherReportCount,
  hasNextPage,
  isFetchingNextPage,
  className,
}: PendingReportsProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'teacherDashboard.teacherReports',
  });

  const reportsCount = teacherReportCount.reduce(
    (acc, report) => acc + (report?.pendingReportsCount ?? 0),
    0,
  );

  return (
    <CollapsableCard
      headerIcon={<DocumentChartBarIcon className="w-6" />}
      title={t('pendingReports')}
      isLoading={isLoading}
      badgeCount={reportsCount}
      isCollapsable={!!teacherReportList.length}
      hasNextPage={hasNextPage}
      isFetchingNextPage={isFetchingNextPage}
      onReachEnd={fetchNextPage}
      className={className}
      listViewSize={216}
    >
      <ConditionalRenderer
        condition={teacherReportList.length}
        fallback={
          <div className="flex justify-center self-center shrink text-center items-center h-32 gap-2 px-5 py-3 flex-col gap-y-2 text-accent font-500">
            <Text className="shrink" text={t('noReportsPhrase')} />
            <Text text="\ (•◡•) /" />
          </div>
        }
      >
        {teacherReportList?.map(item => (
          <li key={item.teacher.id} className="h-fit">
            <Badge count={item.pendingReportsCount} position="-top-1 left-4">
              <AvatarName
                href={`/admin/teachers/?${createSearchParams({
                  search: handleUserFullName(item.teacher),
                })}`}
                user={item.teacher}
              />
            </Badge>
          </li>
        ))}
      </ConditionalRenderer>
    </CollapsableCard>
  );
};
